import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import policyMarkdown from '../../policy.md?raw';

interface PolicyBlock {
  type: 'title' | 'section' | 'subsection' | 'paragraph';
  text: string;
}

const parsePolicy = (content: string): PolicyBlock[] =>
  content
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((text, index): PolicyBlock => {
      if (index === 0) {
        return { type: 'title', text };
      }

      if (/^\d+\.\d+\s+/.test(text)) {
        return { type: 'subsection', text };
      }

      if (/^\d+\.\s+/.test(text)) {
        return { type: 'section', text };
      }

      return { type: 'paragraph', text };
    });

const renderInlineText = (text: string) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);

  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <Link key={`${part}-${index}`} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </Link>
      );
    }

    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();
  const blocks = useMemo(() => parsePolicy(policyMarkdown), []);
  const [titleBlock, ...contentBlocks] = blocks;
  const logoSrc =
    theme.palette.mode === 'dark'
      ? new URL('/images/ops_dark.png', import.meta.url).href
      : new URL('/images/ops_light.png', import.meta.url).href;

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Box
        component="header"
        sx={{
          maxWidth: 1040,
          mx: 'auto',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box
          component={RouterLink}
          to="/login"
          aria-label="Recyclee Ops sign in"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="Recyclee Ops"
            sx={{ height: 46, maxWidth: 180, objectFit: 'contain' }}
          />
        </Box>
        <Button component={RouterLink} to="/login" variant="outlined" sx={{ borderRadius: 1 }}>
          Sign in
        </Button>
      </Box>

      <Paper
        elevation={2}
        sx={{
          maxWidth: 1040,
          mx: 'auto',
          borderRadius: '16px',
          bgcolor: theme.palette.background.paper,
          p: { xs: 2.5, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: 920 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: `${theme.palette.primary.main}14`,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PrivacyTipOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2,
                  mb: 0.75,
                }}
              >
                {titleBlock?.text ?? 'Privacy Policy'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                How Recyclee Ops collects, uses, stores, protects, and shares operational data.
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {contentBlocks.map((block, index) => {
            if (block.type === 'section') {
              return (
                <Box key={`${block.type}-${index}`} sx={{ mt: index === 0 ? 0 : 4 }}>
                  {index > 0 ? <Divider sx={{ mb: 3 }} /> : null}
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 1.5,
                      lineHeight: 1.25,
                    }}
                  >
                    {block.text}
                  </Typography>
                </Box>
              );
            }

            if (block.type === 'subsection') {
              return (
                <Typography
                  key={`${block.type}-${index}`}
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mt: 3,
                    mb: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {block.text}
                </Typography>
              );
            }

            return (
              <Typography
                key={`${block.type}-${index}`}
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.75,
                  mb: 1.5,
                  maxWidth: '76ch',
                }}
              >
                {renderInlineText(block.text)}
              </Typography>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicy;
